import { type FC, useCallback, useId, useState } from "react";
import { useBoolean } from "usehooks-ts";

import { useCanDeleteActions } from "../../../../../utils/delete";

import { DurationDisplayBox } from "./DurationDisplayBox";
import { PageListItem } from "./PageListItem";

import { HoverableEditableText } from "components/hover-widgets/EditableText";
import { HoverableToolbar } from "components/hover-widgets/HoverableToolbar";
import { HideUnlessEditModeButton } from "components/materials/HideUnlessEditModeButton";
import {
  EstimatedTimeMenu,
  TimesType,
} from "components/materials/topic/activity/detail/EstimatedTimeMenu";
import { ActivitySidebarOptionsMenu } from "components/menus/ActivitySidebarOptionsMenu";
import { ActivityPageType, ActivityType } from "components/server-types";
import { t } from "i18n/i18n";
import { Card } from "mds/components/Card";
import { ConfirmDeletionDialog } from "mds/components/ConfirmDeletionDialog";
import { DragAndDrop, DraggableItemType } from "mds/components/DragAndDrop";
import { useOrdered } from "mds/hooks/use-ordered";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectCurrentPage,
  selectCurrentSectionId,
  selectIsEditModeEnabled,
  selectPagesByActivityId,
} from "store/selectors";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import {
  copyActivity,
  isArchived,
  moveActivityWithinSection,
  useActivityActions,
} from "utils/activity";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { useLocalCopy, usePageDragOrdering } from "utils/collections";
import {
  PAGE_LIST_ITEM_FIELDS,
  changeReleaseStatusForPages,
  getReleaseStatus,
  usePageActions,
} from "utils/page";
import { pageUrl } from "utils/urls";

const DEFAULT_TIME_ESTIMATE = 0;

type ActivityCardProps = {
  activity: Pick<
    ActivityType,
    "id" | "category" | "order" | "title" | "estimated_time" | "topic_id"
  >;
  activityIndex: number;
  moveActivityLocally: (item: DraggableItemType, toIndex: number, dropContainerId: string) => void;
  shouldDisplayActivityNumber?: boolean;
};

/* This is the card used to display Lesson and Archive activities. Additional Resources
   and Prep Work activities are displayed via AdditionalResourceList and PrepWorkActivityCard
   respectively. If you are doing an update here, you likely want to check and update the
   other components.
   TODO: We should probably integrate these components into a single ActivityCard component,
   or rename this one as we've introduced a number of bugs due to inconsistently updating
   the different components as the design evolves.
 */
export const ActivityCard: FC<ActivityCardProps> = ({
  activityIndex,
  activity,
  moveActivityLocally,
  shouldDisplayActivityNumber = true,
}) => {
  const accessibleId = useId();
  const { canAuthorSection } = useCapabilities();
  const sectionId = useAppSelector(selectCurrentSectionId);
  const activityPages = useListSelector(
    (s) => selectPagesByActivityId(s, activity.id),
    PAGE_LIST_ITEM_FIELDS,
  );
  const [_pages, setPages] = useLocalCopy(activityPages);
  const currentPage = useObjectSelector(selectCurrentPage, ["activity_id"]);
  const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
  const pages = useOrdered(_pages);
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const activityHasPages = pages.length > 0;

  const { canDeleteActivity } = useCanDeleteActions(activity.topic_id, pages);

  const movePageLocally = usePageDragOrdering(
    activity.id,
    activityPages,
    pages,
    setPages,
    "activity_id",
  );

  const isActive = currentPage?.activity_id === activity.id;
  const { saveActivity, deleteActivity, dropActivity, unarchiveActivity, archiveActivity } =
    useActivityActions();
  const { createPage, dropPage, selectPage, navigateToPage } = usePageActions();

  const {
    value: confirmationDialogIsOpen,
    setTrue: openConfirmationDialog,
    setFalse: closeConfirmationDialog,
  } = useBoolean(false);

  const activityIsArchived = isArchived(activity.category);
  const activityReleaseStatus = getReleaseStatus(pages);

  const createNewPage = () => {
    createPage("activity_page", activity.id);
    trackEvent("Activity - Add page", {
      ...BUTTON_EVENT_CATEGORY,
    });
  };

  const onDropPage = (droppedPage: ActivityPageType) =>
    dropPage(droppedPage, { isActivityScoped: true });

  const onActivityTitleChanged = (newTitle: string) => {
    saveActivity({ id: activity.id, title: newTitle });
  };

  const onActivityEstimatedTimeChanged = (newTime: number) => {
    saveActivity({ id: activity.id, estimated_time: newTime });
  };

  const onDelete = useCallback(() => {
    trackEvent("Activity - Delete activity", {
      ...BUTTON_EVENT_CATEGORY,
    });
    if (activityHasPages) {
      openConfirmationDialog();
    } else {
      deleteActivity(activity);
    }
  }, [activity, deleteActivity, openConfirmationDialog, activityHasPages]);

  if (!sectionId) return null;

  return (
    <Card className="mb-3 mt-2" isActive={isActive} small>
      <DragAndDrop
        draggable={canAuthorSection && isEditModeEnabled && !isEditingTitle}
        dragItemKind="activity"
        dropContainerId={activity.category}
        dropIndex={activityIndex}
        droppable={canAuthorSection && isEditModeEnabled}
        item={activity}
        onDraggedItemHover={moveActivityLocally}
        onDropItem={dropActivity}
      >
        <DragAndDrop
          dragItemKind="page"
          dropContainerId={activity.id}
          dropIndex={0}
          droppable={canAuthorSection && isEditModeEnabled}
          onDraggedItemHover={movePageLocally}
          onDropItem={onDropPage}
        >
          <div className="mb-1 flex w-full items-start justify-between">
            {/* TODO: Fix: students and instructors might see different numbers because unpublished activities are skipped for students */}
            <HoverableToolbar
              className="ml-1 mr-2 flex w-full items-center justify-between"
              contentClassName="mt-0.5 bg-white"
              extraListItems={
                <li>
                  <ActivitySidebarOptionsMenu
                    currentTopicId={activity.topic_id}
                    optionsMenuActions={{
                      onAddPage: createNewPage,
                      onArchive: activityIsArchived ? undefined : () => archiveActivity(activity),
                      onUnarchive: activityIsArchived
                        ? () => unarchiveActivity(activity)
                        : undefined,
                      onDelete,
                      isDeleteDisabled: !canDeleteActivity(activity.id),
                      deleteTooltipText: canDeleteActivity(activity.id)
                        ? undefined
                        : t("tooltip.delete_activity_has_assessments"),
                      onDuplicate: () => copyActivity(activity.id),
                      onMoveTo: (destinationTopicId) =>
                        moveActivityWithinSection(activity.id, destinationTopicId),
                      ...(!activityIsArchived
                        ? { onChangeStatus: () => changeReleaseStatusForPages(pages) }
                        : {}),
                    }}
                    releaseStatus={activityReleaseStatus}
                  />
                </li>
              }
              readOnly={!canAuthorSection}
              reference="activity"
              disableUnlessEditMode
              showEditButton
              uncentered
              onEditModeChange={setIsEditingTitle}
            >
              <h3
                className="my-0 flex w-full cursor-pointer items-center font-semibold"
                id={accessibleId}
                onClick={() => pages.length > 0 && selectPage(pages[0])}
              >
                {shouldDisplayActivityNumber && (
                  <div className="h3 mt-1 self-start pr-1">{activityIndex + 1}.</div>
                )}

                <HoverableEditableText
                  className="pl-[3px]"
                  label={t("glossary.activity_title")}
                  textSize="h3"
                  value={activity.title}
                  disableUnlessEditMode
                  shouldWrap
                  onValueChanged={onActivityTitleChanged}
                />
              </h3>
            </HoverableToolbar>

            {canAuthorSection && isEditModeEnabled && (
              <EstimatedTimeMenu
                selectedTime={(activity.estimated_time || DEFAULT_TIME_ESTIMATE) as TimesType}
                onClick={onActivityEstimatedTimeChanged}
              />
            )}

            {canAuthorSection && !isEditModeEnabled && !!activity.estimated_time && (
              <div className="mt-2">
                <DurationDisplayBox duration={activity.estimated_time} />
              </div>
            )}
          </div>
        </DragAndDrop>

        {pages.length > 0 ? (
          <ol aria-labelledby={accessibleId}>
            {pages.map((page, index) => (
              <PageListItem
                containerId={activity.id}
                key={page.id}
                page={page}
                pageIndex={index}
                onDropPage={onDropPage}
                onEditingTitle={setIsEditingTitle}
                onMovePageLocally={movePageLocally}
                onPageClick={() =>
                  navigateToPage(page, pageUrl(sectionId, page.id, { topicId: activity.topic_id }))
                }
              />
            ))}
          </ol>
        ) : null}
      </DragAndDrop>

      {canAuthorSection && (
        <div className="-ml-1">
          <HideUnlessEditModeButton reference="page" onClick={createNewPage} />
        </div>
      )}

      <ConfirmDeletionDialog
        glossaryKey="activity"
        instanceName={activity.title}
        open={confirmationDialogIsOpen}
        onClose={closeConfirmationDialog}
        onDelete={() => deleteActivity(activity)}
      />
    </Card>
  );
};
